import { useConnection } from "@solana/wallet-adapter-react";
import type { RpcResponseAndContext, TokenAmount } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";

interface BasisMarketsHook {
  rBasisAPR: number | null;
  rBasisRatio: number | null;
}

export const R_BASIS_MINT = new PublicKey("rBsH9ME52axhqSjAVXY3t1xcCrmntVNvP3X16pRjVdM");
export const BASIS_MINT = new PublicKey("3sBX8hj4URsiBCSRV26fEHkake295fQnM44EYKKsSs51");

// Code snippet received from Basis team and refactored to fit our app
export const useBasisMarkets = (): BasisMarketsHook => {
  const { connection } = useConnection();
  const EMISSION_SCHEDULE: number[] = [
    0, // JAN 2022
    30_000_000, // FEB 2022
    30_000_000, // MAR 2022
    20_000_000, // APR 2022
    20_000_000, // MAY 2022
    10_000_000, // JUN 2022
    10_000_000, // JUL 2022
    10_000_000, // AUG 2022
    10_000_000, // SEP 2022
    10_000_000, // OCT 2022
    // TODO: DYNAMIC CALC AFTER THIS
  ];

  const [rBasisSupply, setRBasisSupply] = useState<number | null>(null);
  const [basisSupply, setBasisSupply] = useState<number | null>(null);
  const [blockTimeSeconds, setBlockTimeSeconds] = useState<number | null>(null);
  // get vault contents
  useEffect(() => {
    connection
      .getTokenSupply(R_BASIS_MINT)
      .then((response: RpcResponseAndContext<TokenAmount>) => {
        setRBasisSupply(response.value.uiAmount);
      })
      .catch((e) => {
        console.error(`Unexpected failure to getTokenAccountBalance for ${R_BASIS_MINT}: ${e}`);
      });

    connection
      .getTokenAccountBalance(BASIS_MINT)
      .then((response: RpcResponseAndContext<TokenAmount>) => {
        setBasisSupply(response.value.uiAmount);
      })
      .catch((e) => {
        console.error(`Unexpected failure to getTokenSupply for ${BASIS_MINT}: ${e}`);
      });

    connection
      .getSlot("confirmed")
      .then((slot: number) => {
        return connection.getBlockTime(slot);
      })
      .then((blockTimeSeconds: number | null) => {
        setBlockTimeSeconds(blockTimeSeconds);
      })
      .catch((e) => {
        console.error(`Unexpected failure to getSlot or getBlockTime: ${e}`);
      });
  }, [connection]);

  // get current emissions time details
  const blockDt = DateTime.fromSeconds(blockTimeSeconds || 0);
  const currentMonth = Number(blockDt.toFormat("M"));
  const daysInCurrentMonth = Math.ceil(blockDt.endOf("month").diff(blockDt.startOf("month"), ["days"]).days);

  // determine emissions
  const emissions = EMISSION_SCHEDULE[currentMonth - 1];
  const emissionsPerDay = emissions / daysInCurrentMonth;
  const periodsPerDay = 24 * 6; // ~every 10 minutes
  const R0 = emissionsPerDay / periodsPerDay / (rBasisSupply || 1);

  // APR/APY CALC
  const APR = R0 * periodsPerDay * 365;
  // const APY = aprToApy(APR);

  // RATIO
  let ratio = 0;
  if (rBasisSupply && basisSupply) {
    ratio = basisSupply / rBasisSupply;
  }

  // debug log
  // console.log("log: ", {
  //   currentMonth,
  //   daysInCurrentMonth,
  //   emissions,
  //   emissionsPerDay,
  //   APR,
  //   APY,
  //   ratio,
  // });

  return {
    rBasisAPR: APR * 100,
    rBasisRatio: ratio,
  };
};
